import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { MotifButton, MotifTable } from '@ey-xd/motif-react';
import CustomHeaderTemplate from '../shared/customHeaders/CustomHeaderTemplate';
import PropTypes from 'prop-types';
import AnalyticsEntitlementClientCellRenderer from './customRenderer/AnalyticsEntitlementClientCellRenderer';
import AllClientsSelectedRenderer from './customRenderer/AllClientsSelectedRenderer';

const AnalyticsEntitlementTable = ({
  analyticsEntitlementData,
  handleGridChanges,
  clientCount,
  isSelectedClientChanged,
  handleCancel,
  handleSave,
}) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const rowDataRef = useRef([]);
  rowDataRef.current = rowData;

  // Default column settings
  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    }),
    []
  );

  const handleClientCheckboxChange = useCallback(
    (clientID, rowNode, isHeaderCheckboxSelected) => {
      let updatedData = rowDataRef.current;
      const api = gridRef && gridRef.current ? gridRef.current.api : null;
      if (!api) return;

      if (clientID !== undefined) {
        // Update the data directly
        updatedData = rowDataRef.current.map((newUd) => {
          if (
            newUd.clientID === clientID &&
            newUd.analyticsReportName === rowNode.data.analyticsReportName
          ) {
            return { ...newUd, isAccessGranted: !newUd.isAccessGranted };
          }
          return newUd;
        });

        // Wait for state update, then refresh the specific row
        setTimeout(() => {
          if (api && rowNode) {
            rowNode.setDataValue(
              'isAccessGranted',
              !rowNode.data.isAccessGranted
            );
            api.refreshCells({
              rowNodes: [rowNode],
              columns: ['clientName', 'isAccessGranted'],
              force: true,
            });
          }
        }, 0);

        handleGridChanges(updatedData);
      }
      // parent checkbox is selected (ALL, Selected, None)
      else if (
        clientID === undefined &&
        gridRef &&
        gridRef.current &&
        gridRef.current.api
      ) {
        let reportName = rowNode.key || rowNode.node.key;

        // Update all matching rows
        updatedData = rowDataRef.current.map((row) => {
          if (row.analyticsReportName === reportName) {
            return {
              ...row,
              isAssignedToAllClients: !isHeaderCheckboxSelected,
            };
          }
          return row;
        });

        setRowData(updatedData);

        // Wait for state update, then refresh all rows for this report
        setTimeout(() => {
          if (api) {
            api.refreshCells({
              columns: ['clientName', 'isAssignedToAllClients'],
              force: true,
            });
          }
        }, 0);

        handleGridChanges(updatedData);
      }
    },
    [handleGridChanges]
  );

  // Column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: 'analyticsReportName',
        headerName: 'Analytics Report',
        minWidth: 250,
        valueGetter: (params) => params.data?.analyticsReportName || '',
        headerComponentParams: {
          template: CustomHeaderTemplate('Analytics Report'),
        },
        rowGroup: true,
        hide: true,
      },
      // add in a cell renderer params
      {
        headerName: 'Client',
        field: 'clientName',
        minWidth: 400,
        filterValueGetter: (params) => {
          if (
            params &&
            params.data &&
            params.data.clientName &&
            params.data.clientName.length > 0
          ) {
            return params.data.clientName;
          }
        },
        // remove agGroupCellRenderer usage so expand icon is NOT shown here
        // Use the custom renderer directly (no agGroupCellRenderer) so only the Analytics Report (autoGroupColumnDef) shows the expand icon
        cellRenderer: AnalyticsEntitlementClientCellRenderer,
        cellRendererParams: (params) => {
          const allChildren = params?.node?.allLeafChildren;
          let label = 'Select All';
          let isAssignedToAllClients = false;

          if (Array.isArray(allChildren) && allChildren.length > 0) {
            const selectedCount = allChildren.filter(
              (c) => c?.data && Boolean(c.data.isAccessGranted)
            ).length;
            // Group leaves by analyticsReportCatalogID
            const groupsByCatalog = allChildren.reduce((acc, child) => {
              const key = child?.data?.analyticsReportCatalogID ?? '__unknown';
              if (!acc[key]) acc[key] = [];
              acc[key].push(child);
              return acc;
            }, {});

            // IsAssignedToAllClients -> true only if every catalog group has all its leaves assigned
            isAssignedToAllClients = Object.values(groupsByCatalog).every(
              (group) =>
                group.length > 0 &&
                group.every((c) => Boolean(c?.data?.isAssignedToAllClients))
            );

            if (isAssignedToAllClients || selectedCount === 0) {
              label = 'Assign to all clients';
            } else {
              label = `Assign to all clients (${selectedCount} out of ${clientCount} clients selected)`;
            }
          }

          return {
            onClientCheckboxChange: handleClientCheckboxChange,
            label: label,
            isAssignedToAllClients: isAssignedToAllClients,
          };
        },
        headerComponentParams: { template: CustomHeaderTemplate('Client') },
      },

      {
        field: 'isAccessGranted',
        hide: true,
      },
      {
        field: 'isAssignedToAllClients',
        hide: true,
      },
    ],
    [clientCount, handleClientCheckboxChange]
  );

  // Group column definitions
  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: 'Analytics Report', // or '' to hide the "Group" label
      cellRendererParams: { suppressCount: true },
      minWidth: 250,
      filterValueGetter: (params) => {
        if (
          params &&
          params.data &&
          params.data.analyticsReportName &&
          params.data.analyticsReportName.length > 0
        ) {
          return params.data.analyticsReportName;
        }
      },
      headerComponentParams: {
        template: CustomHeaderTemplate('Analytics Report'),
      },
      cellRendererSelector: (params) => {
        const allChildren = params?.node?.allLeafChildren;
        let isAssignedToAllClients = false;

        if (Array.isArray(allChildren) && allChildren.length > 0) {
          // Group leaves by analyticsReportCatalogID
          const groupsByCatalog = allChildren.reduce((acc, child) => {
            const key = child?.data?.analyticsReportCatalogID ?? '__unknown';
            if (!acc[key]) acc[key] = [];
            acc[key].push(child);
            return acc;
          }, {});

          // IsAssignedToAllClients -> true only if every catalog group has all its leaves assigned
          isAssignedToAllClients = Object.values(groupsByCatalog).every(
            (group) =>
              group.length > 0 &&
              group.every((c) => Boolean(c?.data?.isAssignedToAllClients))
          );

          if (!isAssignedToAllClients) {
            return {
              component: 'agGroupCellRenderer',
            };
          } else {
            return {
              component: AllClientsSelectedRenderer,
              params: { analyticsReportName: params.data?.analyticsReportName },
            };
          }
        }
      },
    }),
    []
  );

  useEffect(() => {
    const normalized = (analyticsEntitlementData || []).map((row) => ({
      ...row,
      isAssignedToAllClients:
        typeof row.isAssignedToAllClients === 'boolean'
          ? row.isAssignedToAllClients
          : false,
      isAccessGranted:
        typeof row.isAccessGranted === 'boolean' ? row.isAccessGranted : false,
    }));
    setRowData(normalized);

    if (gridRef && gridRef.current && gridRef.current.api) {
      gridRef.current.api.redrawRows();
      gridRef.current.api.refreshHeader();
    }
  }, [analyticsEntitlementData]);

  const onCellKeyDown = useCallback(
    (event) => {
      if (
        (event.event.key === 'Enter' || event.event.key === ' ') &&
        event.colDef.field == 'clientName'
      ) {
        let allChildren = event?.node?.allLeafChildren || [];
        let isHeaderCheckboxSelected =
          allChildren &&
          allChildren.length > 0 &&
          allChildren.every((c) => c?.data && Boolean(c.data.isAccessGranted));

        handleClientCheckboxChange(
          event?.data?.clientID,
          event,
          isHeaderCheckboxSelected || false
        );
      }
    },
    [handleClientCheckboxChange]
  );

  const overlayNoRowsTemplate = `<div>No active analytics reports</div>`;
  const hasData =
    analyticsEntitlementData && analyticsEntitlementData.length > 0;
  return (
    <div className="row mb-10">
      <div className="col">
        <div
          data-testid="grdUtilities"
          className={`ag-theme-quartz motif-table ${hasData ? 'table-bd-half-height-width' : 'empty-table-container'}`}
        >
          <MotifTable
            zebra={true}
            compact={true}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            accentedSort={true}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            ref={gridRef}
            pagination={true}
            suppressPaginationPanel={true}
            paginationPageSize={100}
            autoGroupColumnDef={autoGroupColumnDef}
            overlayNoRowsTemplate={overlayNoRowsTemplate}
            onCellKeyDown={onCellKeyDown}
          />

          {hasData && (
            <div className="mt-3">
              <span className="right mb-3">
                <MotifButton
                  variant="primary"
                  type="button"
                  data-testid="btnSubmit"
                  disabled={!isSelectedClientChanged}
                  style={{ maxHeight: '3rem' }}
                  onClick={handleSave}
                >
                  Submit
                </MotifButton>
              </span>
              <span className="right mb-3 mr-3 ml-5">
                <MotifButton
                  variant="secondary"
                  type="button"
                  data-testid="btnCancel"
                  style={{ maxHeight: '3rem' }}
                  onClick={handleCancel}
                >
                  Cancel
                </MotifButton>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AnalyticsEntitlementTable.propTypes = {
  analyticsEntitlementData: PropTypes.arrayOf(PropTypes.object),
  handleGridChanges: PropTypes.func,
  clientCount: PropTypes.number,
  isSelectedClientChanged: PropTypes.bool,
  handleCancel: PropTypes.func,
  handleSave: PropTypes.func,
};

export default AnalyticsEntitlementTable;
